/**
 * Tenant & Secrets API Service
 * Connects to the serverless tenant management backend.
 * Base URL: https://5i8dsflcnb.execute-api.us-east-1.amazonaws.com
 * Auth: Bearer token (Cognito IdToken)
 */

import { createDebugFetch } from '../lib/debug-console/interceptors'

const TENANT_API_BASE = 'https://5i8dsflcnb.execute-api.us-east-1.amazonaws.com'

// Debug-wrapped fetch — all tenant API calls appear in Ctrl+Shift+D console
const debugFetch = createDebugFetch('TENANT-API')

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface Tenant {
  tenant_id: string
  name: string
  industry?: string
  email: string
  active_plan?: string
  status?: 'active' | 'inactive' | 'deleted'
  created_at?: string
  updated_at?: string
}

export interface TenantCreatePayload {
  name: string
  industry?: string
  email: string
}

export interface TenantUpdatePayload {
  name?: string
  industry?: string
  email?: string
  active_plan?: string
  status?: string
}

export interface TenantSecret {
  service: string
  created_at?: string
  updated_at?: string
  // Credentials are never returned in full — only metadata
  has_credentials?: boolean
}

export interface SecretCreatePayload {
  credentials: Record<string, string>
}

export interface ApiError {
  error: string
  statusCode: number
}

// ─── Helper ─────────────────────────────────────────────────────────────────────

async function tenantFetch<T>(
  path: string,
  options: RequestInit = {},
  token?: string,
): Promise<{ data: T | null; error: string | null; status: number }> {
  const authToken = token || getStoredToken()
  if (!authToken) {
    return {
      data: null,
      error: 'No auth token available. Please log in.',
      status: 401,
    }
  }

  try {
    const res = await debugFetch(`${TENANT_API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        ...(options.headers || {}),
      },
    })

    if (!res.ok) {
      let errorMsg = `HTTP ${res.status}`
      try {
        const errBody = await res.json()
        errorMsg = errBody.error || errBody.message || errorMsg
      } catch {}
      return { data: null, error: errorMsg, status: res.status }
    }

    // DELETE might return 204 with no body
    if (res.status === 204) {
      return { data: null, error: null, status: 204 }
    }

    const data = await res.json()
    return { data, error: null, status: res.status }
  } catch (err: any) {
    return {
      data: null,
      error: err.message || 'Network error',
      status: 0,
    }
  }
}

function getStoredToken(): string | null {
  // Try to get the Cognito IdToken from localStorage (set by auth.ts)
  return localStorage.getItem('vsm_auth_token')
}

// ─── Health ─────────────────────────────────────────────────────────────────────

export async function checkHealth(): Promise<{
  ok: boolean
  data?: any
  error?: string
}> {
  try {
    const res = await debugFetch(`${TENANT_API_BASE}/health`)
    const data = await res.json()
    return { ok: res.ok, data }
  } catch (err: any) {
    return { ok: false, error: err.message }
  }
}

// ─── Tenants CRUD ───────────────────────────────────────────────────────────────

export async function listTenants(token?: string) {
  return tenantFetch<Tenant[]>('/tenants', { method: 'GET' }, token)
}

export async function getTenant(tenantId: string, token?: string) {
  return tenantFetch<Tenant>(`/tenants/${tenantId}`, { method: 'GET' }, token)
}

export async function getTenantByEmail(email: string, token?: string) {
  return tenantFetch<Tenant>(
    `/tenants/by-email?email=${encodeURIComponent(email)}`,
    { method: 'GET' },
    token,
  )
}

export async function createTenant(
  payload: TenantCreatePayload,
  token?: string,
) {
  return tenantFetch<Tenant>(
    '/tenants',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    token,
  )
}

export async function updateTenant(
  tenantId: string,
  payload: TenantUpdatePayload,
  token?: string,
) {
  return tenantFetch<Tenant>(
    `/tenants/${tenantId}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    token,
  )
}

export async function deleteTenant(tenantId: string, token?: string) {
  return tenantFetch<null>(`/tenants/${tenantId}`, { method: 'DELETE' }, token)
}

// ─── Secrets ────────────────────────────────────────────────────────────────────

export async function listSecrets(tenantId: string, token?: string) {
  return tenantFetch<TenantSecret[]>(
    `/tenants/${tenantId}/secrets`,
    { method: 'GET' },
    token,
  )
}

export async function createOrUpdateSecret(
  tenantId: string,
  service: string,
  credentials: Record<string, string>,
  token?: string,
) {
  return tenantFetch<TenantSecret>(
    `/tenants/${tenantId}/secrets/${encodeURIComponent(service)}`,
    {
      method: 'POST',
      body: JSON.stringify({ credentials }),
    },
    token,
  )
}

export async function deleteSecret(
  tenantId: string,
  service: string,
  token?: string,
) {
  return tenantFetch<null>(
    `/tenants/${tenantId}/secrets/${encodeURIComponent(service)}`,
    { method: 'DELETE' },
    token,
  )
}

// ─── Convenience: Full tenant with secrets ──────────────────────────────────────

export async function getTenantWithSecrets(tenantId: string, token?: string) {
  const [tenantRes, secretsRes] = await Promise.all([
    getTenant(tenantId, token),
    listSecrets(tenantId, token),
  ])
  return {
    tenant: tenantRes.data,
    secrets: secretsRes.data || [],
    error: tenantRes.error || secretsRes.error,
  }
}
